import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useRunReportQuery,
  useGetReportSpecificFieldsQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { jsTimeToDBTime } from "../../../utils/dateTime";
import ChartWrapper from "../components/chartWrapper";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import WidgetChartAreaGenerator from "../components/widgetChartAreaGenerator";
import { useOverviewContext } from "../context/overview.context";
import { EWidgetChartTypes } from "../types/overview.types";
import { useGroupsAndAssetsUtils } from "../hooks/overviewScreenUtils";

const chartColors = ["#FF5959", "#FFD059", "#51A3A3"]; //"#3D78B1"

const reportId = 74;
const defaultChartType = EWidgetChartTypes.VERTICAL_BAR_GRAPH;

const graphXAxisDataKey = "title";
const graphYAxisDataKey = "value";

const WidgetAssetMaintenance: FC<IWidgetProps> = ({ widgetType }) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.widgets.enum_4",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType);

  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isUninitialized: isUninitializedSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      // type: REPORT_TYPES.CUSTOM,
      report_id: reportId,
    },
    { refetchOnMountOrArgChange: true }
  );

  // const { data: dataReport, isFetching } = useRunReportQuery(
  //   {
  //     id: reportId.toString(),
  //     // fields: '443,444,447'.split(','),
  //     // ordering: '447',

  //     page: 1,
  //     page_size: 10,

  //     // filters
  //     device_groups:
  //       allGroupsSelected === false && !!selectedGroups
  //         ? selectedGroups
  //         : undefined,
  //     start_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[0]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[0]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     end_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[1]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[1]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     ds_time_range: allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.value
  //   },
  //   {
  //     skip: isUninitializedSpecificReport || isFetchingSpecificReport,
  //     refetchOnMountOrArgChange: true
  //   }
  // );

  const {
    reportData: dataReport,
    isFetching,
    isError,
    reportError,
  } = useGroupsAndAssetsUtils(selectedDataType, reportId, selectedAssets);

  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  useEffect(() => {
    if (
      !!dataReport &&
      !!dataReport?.results &&
      !!dataSpecificReport?.report_fields &&
      !!dataReport?.results?.[0]
    ) {
      setComputedChartData(
        Object.keys(dataReport?.results?.[0]).map((columnKeys) => ({
          [graphXAxisDataKey]: dataSpecificReport?.report_fields?.find(
            (field) => field.field.id === parseInt(columnKeys)
          )?.field?.name,
          [graphYAxisDataKey]: dataReport?.results?.[0][columnKeys],
        }))
      );
    }
  }, [dataReport, dataSpecificReport]);

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}

      {/* Labels */}
      <div className="mb-4 mt-4 flex flex-col gap-2">
        <div className="flex justify-between">
          <span>{t("total_assets_in_fleet")}</span>
          <span className="text-accent-blue-dark font-bold">
            {!!dataReport && !!dataReport?.results
              ? computedChartData?.find(
                  (item) => item[graphXAxisDataKey] === "total_count"
                )?.value
              : 0}
          </span>
        </div>
      </div>

      {(!!dataReport && dataReport?.results?.length > 0) || isFetching ? (
        <>
          {/* Chart Wrapper */}
          {isFetching ? (
            <ChartWrapper heightClass="pt-[45%]">
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </ChartWrapper>
          ) : (
            <>
              <WidgetChartAreaGenerator
                barChartMargin={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? { top: 0, right: 10, bottom: 0, left: -10 }
                    : { top: 0, right: 0, left: -35, bottom: 0 }
                }
                cellItemsForSectionColor={
                  !!dataReport &&
                  !!dataReport?.results &&
                  !!dataSpecificReport?.report_fields &&
                  !!dataReport?.results?.[0]
                    ? Object.keys(dataReport?.results?.[0])
                    : []
                }
                chartColors={chartColors}
                chartType={selectedChartType}
                chartWrapperHeightClass={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? "pt-[45%]"
                    : "pt-[45%]"
                }
                computedChartData={computedChartData
                  .filter((item) => item[graphXAxisDataKey] !== "total_count")
                  .sort((a, b) => b.value - a.value)}
                legendLabelFormatter={(value: any) => t(`${value}`)}
                t={t}
                widgetType={widgetType}
                xAxisTickLabelWidth={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? 40
                    : 80
                }
              />
              {/* <SwitchChartButton onClick={() => {
                  dispatch(setModalsData({ ...modalsState, switchChartTypeForWidget: widgetType, showSwitchChartType: true }))
                }} />
                <ResponsiveContainer className={'absolute top-0 left-0 w-full h-full'}>
                  <BarChart
                    width={500}
                    height={500 * 0.45}
                    data={
                      computedChartData
                        .filter((item) => item[graphXAxisDataKey] !== 'total_count')
                    }
                    margin={{
                      top: 0,
                      right: 0,
                      left: -35,
                      bottom: 0,
                    }}
                  // barCategoryGap={10}
                  >
                    {
                      // <defs>
                      // <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                      // <stop offset="0%" stopColor={chartColors.positive.light} stopOpacity={1} />
                      // <stop offset="100%" stopColor={chartColors.positive.dark} stopOpacity={1} />
                      // </linearGradient>
                      // <linearGradient id="colorNegative" x1="0" y1="1" x2="0" y2="0">
                      // <stop offset="0%" stopColor={chartColors.negative.light} stopOpacity={1} />
                      // <stop offset="100%" stopColor={chartColors.negative.dark} stopOpacity={1} />
                      // </linearGradient>
                      // </defs>
                    }
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey={graphXAxisDataKey} style={{ fontSize: '8px' }}
                      // tickFormatter={(value) => t(value)}
                      tick={(props) => <ChartsCustomXAxisTick t={t} {...props} />}
                    // angle={-45}
                    />
                    <YAxis dataKey={graphYAxisDataKey} style={{ fontSize: '8px' }} />
                    {
                      // <Tooltip />
                      // <Legend />
                      // <ReferenceLine y={0} stroke="#000" />
                    }
                    <Bar
                      dataKey={graphYAxisDataKey}
                      fill="#8884d8"
                    // barSize={25}
                    >
                      {!!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields && !!dataReport?.results?.[0] &&
                        Object.keys(dataReport?.results?.[0])
                          ?.map((resultItem, index) => (
                            <Cell
                              cursor="pointer"
                              fill={chartColors[index % chartColors.length]}
                              key={`cell-${index}`}
                              radius={5}
                            />
                          ))}
                    </Bar>
                    {
                      // <Bar dataKey="uv" fill="#82ca9d" />
                    }
                  </BarChart>
                </ResponsiveContainer> */}
            </>
          )}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetAssetMaintenance;
